function openAccordionItem(item) {
  if (!item || item.classList.contains('is-open')) return;
  item.classList.add('is-open');
  var btn = item.querySelector('.bushnell-acc__trigger');
  if (btn) btn.setAttribute('aria-expanded', 'true');
}

/** Resolve a nav link to its target element.
 *  1. data-scroll CSS selector (e.g. .bushnell-product-videos)
 *  2. href hash exact match
 *  3. If hash contains "template--NUMBER__SUFFIX", match by suffix: [id$="__SUFFIX"]
 *     so the same link works across templates without updating the number. */
function resolveTarget(link) {
  const sel = link.dataset.scroll;
  if (sel) return document.querySelector(sel);
  try {
    const hash = new URL(link.href, location.href).hash;
    if (!hash) return null;
    const exact = document.querySelector(hash);
    if (exact) return exact;
    const suffixMatch = hash.match(/__([^#]+)$/);
    if (suffixMatch) {
      return document.querySelector('[id$="__' + suffixMatch[1] + '"]');
    }
    return null;
  } catch (_) { return null; }
}

class BushnellProductNav {
  /** @param {HTMLElement} nav */
  constructor(nav) {
    this.nav = nav;
    /** @type {HTMLAnchorElement[]} */
    this.links = /** @type {HTMLAnchorElement[]} */ ([...nav.querySelectorAll('.bushnell-pnav__link')]);

    // Point each href at the id that really exists on the page (ids carry the theme
    // id / app block id, which change per theme), so no link targets a missing anchor.
    this.links.forEach(link => {
      const target = resolveTarget(link);
      if (!target) {
        // Section was removed from this template: drop the tab instead of leaving a dead anchor.
        const item = link.closest('li');
        if (item) item.remove();
      } else if (target.id && link.getAttribute('href') !== '#' + target.id) {
        link.setAttribute('href', '#' + target.id);
      }
    });
    this.links = this.links.filter(link => link.isConnected);

    /** @type {Element[]} */
    this.anchors = this.links
      .map(link => resolveTarget(link))
      .filter(/** @returns {el is Element} */ el => el !== null);

    if (this.anchors.length) {
      this.observer = new IntersectionObserver(
        this.onIntersect.bind(this),
        { rootMargin: '-20% 0px -60% 0px' }
      );
      this.anchors.forEach(el => this.observer && this.observer.observe(el));
    }

    this.links.forEach(link => {
      link.addEventListener('click', /** @param {MouseEvent} e */ e => {
        const target = resolveTarget(link);
        if (!target) return;
        e.preventDefault();
        const navH = this.nav.offsetHeight;
        const top = target.getBoundingClientRect().top + scrollY - navH - 8;
        window.scrollTo({ top, behavior: 'smooth' });
        if (target.classList.contains('bushnell-acc__item')) openAccordionItem(target);
      });
    });

    /** @type {HTMLElement | null} */
    this.list = nav.querySelector('.bushnell-pnav__list');
    if (this.list) this._initScrollHints(this.list);

    if (nav.classList.contains('bushnell-pnav--sticky')) {
      const sentinel = nav.previousElementSibling;
      if (sentinel && sentinel.classList.contains('bushnell-pnav__sentinel')) {
        this._initSticky(/** @type {HTMLElement} */ (sentinel));
      }
    }
  }

  /** Toggle can-scroll-left/right so fades + arrows signal hidden items.
   *  @param {HTMLElement} list */
  _initScrollHints(list) {
    const updateHints = () => {
      const max = list.scrollWidth - list.clientWidth;
      this.nav.classList.toggle('can-scroll-left', list.scrollLeft > 2);
      this.nav.classList.toggle('can-scroll-right', list.scrollLeft < max - 2);
    };

    this.nav.querySelectorAll('.bushnell-pnav__arrow').forEach(btn => {
      btn.addEventListener('click', () => {
        const dir = btn.classList.contains('bushnell-pnav__arrow--prev') ? -1 : 1;
        list.scrollBy({ left: dir * list.clientWidth * 0.7, behavior: 'smooth' });
      });
    });

    list.addEventListener('scroll', updateHints, { passive: true });
    window.addEventListener('resize', updateHints, { passive: true });
    window.addEventListener('load', updateHints, { once: true });
    requestAnimationFrame(updateHints);
  }

  /** One-time "peek": slide the list left a little and back so users see it scrolls. */
  _peek() {
    const list = this.list;
    if (this._peeked || !list || !this.nav.classList.contains('can-scroll-right')) return;
    if (list.scrollLeft > 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this._peeked = true;
    list.scrollTo({ left: 48, behavior: 'smooth' });
    setTimeout(() => list.scrollTo({ left: 0, behavior: 'smooth' }), 450);
  }

  /** @param {HTMLElement} sentinel */
  _initSticky(sentinel) {
    let navTop = -1;

    const calcTop = () => {
      this.nav.classList.remove('is-stuck');
      sentinel.style.height = '0';
      navTop = sentinel.getBoundingClientRect().top + window.scrollY;
    };

    const update = () => {
      if (navTop < 0) return;
      const stuck = window.scrollY >= navTop;
      if (this.nav.classList.contains('is-stuck') === stuck) return;
      this.nav.classList.toggle('is-stuck', stuck);
      sentinel.style.height = stuck ? this.nav.offsetHeight + 'px' : '0';
      if (stuck) this._peek();
    };

    requestAnimationFrame(() => { calcTop(); update(); });
    window.addEventListener('load', () => { calcTop(); update(); }, { once: true });
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', () => { calcTop(); update(); }, { passive: true });
  }

  /** @param {IntersectionObserverEntry[]} entries */
  onIntersect(entries) {
    entries.forEach(/** @param {IntersectionObserverEntry} entry */ entry => {
      if (!entry.isIntersecting) return;
      const idx = this.anchors.indexOf(entry.target);
      if (idx === -1) return;
      this.links.forEach(l => l.classList.remove('is-active'));
      const activeLink = this.links[idx];
      if (activeLink) activeLink.classList.add('is-active');
    });
  }
}

function initNavs() {
  document.querySelectorAll('.bushnell-pnav').forEach(el => {
    if (el instanceof HTMLElement) new BushnellProductNav(el);
  });
  if (location.hash) {
    var hashTarget = document.querySelector(location.hash);
    if (hashTarget && hashTarget.classList.contains('bushnell-acc__item')) openAccordionItem(hashTarget);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNavs);
} else {
  initNavs();
}
